/**
 * MobileLayout – Optimized Bottom Nav + Clean Header + Context Navigation
 * Max 5 bottom nav items. Context-aware when inside a project.
 */
import { Outlet, NavLink, useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { useTranslation } from "../i18n/LanguageProvider";
import { useNotifications } from "../hooks/useNotifications";
import { useEffect, useState } from "react";
import {
  Clock, BarChart3, Briefcase, LogOut, Bell, BellOff, X,
  LayoutDashboard, MoreHorizontal, Smartphone, User, Edit3, Globe, Check, Brain,
  Timer, ListTodo, ScanLine, CalendarDays, KeyRound, ScrollText, Settings,
} from "lucide-react";
import { MobileQuickAppointmentFAB } from "../components/QuickAppointmentCreate";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import OfflineStatusBar from "../components/OfflineStatusBar";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { useGreeting } from "../hooks/useGreeting";
import AppBackground from "../components/AppBackground";
import { BUILD_VERSION, BUILD_DATE } from "../buildInfo";

const mobileQueryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const API = "/api";

/* ── Global nav items (max 5) ── label = i18n key (resolved at render time) ── */
const ALL_NAV = [
  { to: "/app/m/projekte", icon: Briefcase, labelKey: "nav.projekte", labelFallback: "Projekte", testId: "m-nav-projekte", visId: "projekte" },
  { to: "/app/m/eingabe", icon: Edit3, labelKey: "nav.tagesuebersicht", labelFallback: "Stundenzettel", testId: "m-nav-eingabe", visId: "eingabe" },
  { to: "/app/m/stundenkonto", icon: BarChart3, labelKey: "nav.stundenkonten", labelFallback: "Stundenkonto", testId: "m-nav-stundenkonto", visId: "stundenkonto" },
  { to: "/app/m/dashboard-admin", icon: LayoutDashboard, labelKey: "nav.admindashboard", labelFallback: "Dashboard", testId: "m-nav-admindashboard", adminOnly: true, visId: "admin_dashboard" },
];

/* ── "Mehr" menu items (overflow) ── */
const MORE_ITEMS = [
  { to: "/app/m/stempeluhr", labelKey: "nav.stempeluhr", labelFallback: "Stempeluhr", visId: "stempeln", icon: Timer },
  { to: "/app/m/meine-aufgaben", labelKey: "m.meine_aufgaben", labelFallback: "Meine Aufgaben", visId: "meine_aufgaben", icon: ListTodo },
  { to: "/app/m/doc-scanner", labelKey: "m.doc_scanner", labelFallback: "Dokument scannen", visId: "doc_scanner", icon: ScanLine },
  { to: "/app/m/einsatzplanung", labelKey: "nav.einsatzplanung", labelFallback: "Einsatzplanung", visId: "einsatzplanung", icon: CalendarDays },
  { to: "/app/nina", labelKey: "nav.nina", labelFallback: "Nina", visId: "nina_studio", ninaGuarded: true, icon: Brain },
  { to: "/app/m/passwort-tresor", labelKey: "nav.passworttresor", labelFallback: "Passwort-Tresor", adminOnly: true, visId: "passwort_tresor", icon: KeyRound },
  { to: "/app/m/system-historie", labelKey: "nav.systemhistorie", labelFallback: "System-Historie", adminOnly: true, visId: "system_historie", icon: ScrollText },
  { to: "/app/app-einstellungen", labelKey: "nav.appeinstellungen", labelFallback: "App-Einstellungen", adminOnly: true, visId: "app_einstellungen", icon: Settings },
];

export default function MobileLayout() {
  const { user, logout } = useAuth();
  const { t, lang, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const { settings, updateSettings, permissionState, requestPermission } = useNotifications(authFetch, API);
  const push = usePushNotifications();
  const { text: greetingText } = useGreeting(user);

  const isAdmin = user?.role === "admin";
  const [ninaMenuEnabled, setNinaMenuEnabled] = useState(true);
  const [ninaCanAccess, setNinaCanAccess] = useState(false);

  useEffect(() => {
    let alive = true;
    authFetch("/api/nina-engine/settings/menu-visibility")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        if (d && typeof d.enabled_for_admins === "boolean") setNinaMenuEnabled(d.enabled_for_admins);
      })
      .catch(() => {});
    authFetch("/api/nina-engine/access")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        setNinaCanAccess(!!d?.access);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };

  const handleEnableNotifications = async () => {
    const perm = await requestPermission();
    if (perm === "granted") {
      new Notification("Benachrichtigungen aktiviert", {
        body: "Sie erhalten jetzt Erinnerungen.",
        icon: "/favicon.ico",
      });
    }
  };

  // Filter nav items based on permissions + time tracking mode
  const isStundenzettelMode = user?.time_tracking_mode === "stundenzettel";
  const filterByPerms = (items) => items.filter((item) => {
    // Hide Stempeln tab when user is in Stundenzettel mode
    if (item.hideForStundenzettel && isStundenzettelMode) return false;
    const mVis = user?.mobile_visibility;
    if (mVis && item.visId && mVis[item.visId] === false) return false;
    if (item.ninaGuarded) {
      if (user?.role !== "admin") return false;
      if (!ninaMenuEnabled) return false;
      const hasAccess = ninaCanAccess || !!user?.permissions?.nina_access;
      if (!hasAccess) return false;
      return true;
    }
    if (item.adminOnly) return isAdmin;
    return true;
  });

  const visibleNav = filterByPerms(ALL_NAV);
  const visibleMore = filterByPerms(MORE_ITEMS);
  const hasMore = visibleMore.length > 0;

  return (
    <QueryClientProvider client={mobileQueryClient}>
    <div className="fixed inset-0 flex flex-col bg-transparent" data-testid="mobile-layout">
      <AppBackground />
      {/* ── Top bar – Clean ── */}
      <div className="flex items-center justify-between px-4 flex-shrink-0 safe-area-top tga-mobile-chrome"
        style={{ minHeight: 56, paddingTop: 6, paddingBottom: 6, boxShadow: "0 2px 10px rgba(0,0,0,0.25)" }}
      >
        <div className="flex flex-col gap-0">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate("/app/m/projekte")} data-testid="m-logo">
            <img src="/logo-dark.png" alt="TGAone" className="h-7 w-auto brightness-200" />
            <span className="text-sm font-semibold text-white tracking-tight">TGAone</span>
          </div>
          {greetingText && (
            <span className="text-[13px] font-medium tracking-tight pl-0.5" style={{ color: "rgba(255,255,255,0.55)" }} data-testid="m-greeting">
              {greetingText}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-400 hidden sm:inline mr-1">{user?.name}</span>
          {/* Sprach-Switcher */}
          <button
            onClick={() => setShowLangMenu((v) => !v)}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1"
            data-testid="m-lang-btn"
            aria-label={t("lang.title")}
            title={t("lang.title")}
          >
            <Globe className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-wider">{lang}</span>
          </button>
          <button onClick={() => navigate("/app/m/profil")} className="p-2 rounded-lg text-gray-500 hover:text-gray-300 transition-colors" data-testid="m-profile-btn">
            <User className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowNotifSettings(!showNotifSettings)}
            className={`relative p-2 rounded-lg transition-colors ${settings.enabled ? "text-blue-400" : "text-gray-500 hover:text-gray-300"}`}
            data-testid="m-notif-toggle"
          >
            {settings.enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </button>
          <button onClick={handleLogout} className="p-2 rounded-lg text-gray-500 hover:text-red-400 transition-colors" data-testid="m-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Sprach-Switcher Mini-Menü */}
      {showLangMenu && (
        <div
          className="absolute right-3 top-14 z-[60] w-44 rounded-xl border border-white/10 bg-[#1a1d27] shadow-2xl overflow-hidden"
          data-testid="m-lang-menu"
        >
          <div className="px-3 py-2 border-b border-white/8 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            {t("lang.title")}
          </div>
          {[
            { code: "de", labelKey: "lang.de", flag: "🇩🇪" },
            { code: "sk", labelKey: "lang.sk", flag: "🇸🇰" },
            { code: "en", labelKey: "lang.en", flag: "🇬🇧" },
          ].map((opt) => (
            <button
              key={opt.code}
              onClick={() => { setLanguage(opt.code); setShowLangMenu(false); }}
              className="w-full px-3 py-2.5 flex items-center gap-2 text-left text-sm text-gray-100 hover:bg-white/[0.06] active:bg-white/10"
              data-testid={`m-lang-opt-${opt.code}`}
            >
              <span className="text-base leading-none">{opt.flag}</span>
              <span className="flex-1">{t(opt.labelKey)}</span>
              {lang === opt.code && <Check className="w-4 h-4 text-emerald-400" />}
            </button>
          ))}
        </div>
      )}

      {/* Notification Settings Panel */}
      {showNotifSettings && (
        <div className="absolute inset-x-0 top-12 z-50 bg-[#1a1d27] border-b border-white/10 shadow-lg px-4 py-4" data-testid="notif-settings-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Benachrichtigungen</h3>
            <button onClick={() => setShowNotifSettings(false)} className="text-gray-500 hover:text-gray-300"><X className="w-4 h-4" /></button>
          </div>
          {permissionState !== "granted" ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-400">Aktivieren Sie Benachrichtigungen, um Erinnerungen zu erhalten.</p>
              <button onClick={handleEnableNotifications} className="w-full h-10 bg-blue-600 text-white rounded-lg text-sm font-medium active:bg-blue-700" data-testid="enable-notif-btn">
                <Bell className="w-4 h-4 inline mr-2" />Benachrichtigungen aktivieren
              </button>
              {permissionState === "denied" && (
                <p className="text-[10px] text-red-400">Benachrichtigungen wurden im Browser blockiert. Bitte in den Browser-Einstellungen erlauben.</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <label className="flex items-center justify-between" data-testid="notif-clockout-toggle">
                <div>
                  <p className="text-sm text-white font-medium">Ausstempel-Erinnerung</p>
                  <p className="text-[10px] text-gray-500">Nach {settings.clockOutAfterHours}h Arbeitszeit</p>
                </div>
                <input type="checkbox" checked={settings.clockOutReminder} onChange={(e) => updateSettings({ clockOutReminder: e.target.checked })}
                  className="rounded border-gray-600 bg-white/10 text-blue-500 w-5 h-5" />
              </label>
              {settings.clockOutReminder && (
                <div className="pl-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">Nach</span>
                  <select value={settings.clockOutAfterHours} onChange={(e) => updateSettings({ clockOutAfterHours: Number(e.target.value) })}
                    className="h-8 border border-white/10 bg-white/[0.06] text-white rounded-md text-sm px-2" data-testid="notif-hours-select">
                    {[6, 7, 8, 9, 10, 11, 12].map((h) => (<option key={h} value={h}>{h} Stunden</option>))}
                  </select>
                </div>
              )}
              <label className="flex items-center justify-between" data-testid="notif-einsatzplan-toggle">
                <div>
                  <p className="text-sm text-white font-medium">Einsatzplan-Änderungen</p>
                  <p className="text-[10px] text-gray-500">Neue Zuweisungen und Änderungen</p>
                </div>
                <input type="checkbox" checked={settings.einsatzplanChanges} onChange={(e) => updateSettings({ einsatzplanChanges: e.target.checked })}
                  className="rounded border-gray-600 bg-white/10 text-blue-500 w-5 h-5" />
              </label>
              {push.supported && (
                <label className="flex items-center justify-between" data-testid="push-notif-toggle">
                  <div>
                    <p className="text-sm text-white font-medium flex items-center gap-1.5">
                      <Smartphone className="w-3.5 h-3.5 text-blue-400" />Push-Benachrichtigungen
                    </p>
                    <p className="text-[10px] text-gray-500">Bei neuen Aufgaben und Statusaenderungen</p>
                  </div>
                  <input type="checkbox" checked={push.isSubscribed} disabled={push.loading}
                    onChange={(e) => e.target.checked ? push.subscribe() : push.unsubscribe()}
                    className="rounded border-gray-600 bg-white/10 text-blue-500 w-5 h-5" />
                </label>
              )}
              <button onClick={() => updateSettings({ enabled: false })}
                className="w-full h-9 border border-white/10 rounded-lg text-xs text-gray-400 font-medium hover:bg-white/5" data-testid="disable-notif-btn">
                Alle Benachrichtigungen deaktivieren
              </button>
            </div>
          )}
        </div>
      )}

      {/* "Mehr" Menu Overlay */}
      {showMore && (
        <div className="fixed inset-0 z-[80]" onClick={() => setShowMore(false)} data-testid="m-more-overlay">
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute bottom-20 left-4 right-4 bg-[#1c1f28] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 duration-150" onClick={e => e.stopPropagation()}>
            {visibleMore.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.to} to={item.to} onClick={() => setShowMore(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-5 py-4 text-sm font-medium border-b border-white/5 last:border-0 transition-colors ${isActive ? "text-blue-400 bg-blue-500/10" : "text-gray-300 active:bg-white/5"}`}
                  data-testid={`m-more-${item.visId}`}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {t(item.labelKey, item.labelFallback)}
                </NavLink>
              );
            })}
            <div className="px-5 py-3 border-t border-white/5 text-[10px] text-gray-500 flex items-center justify-between" data-testid="m-build-info">
              <span>Version</span>
              <span className="font-mono text-gray-400" data-testid="m-build-version">{BUILD_VERSION}</span>
            </div>
            <div className="px-5 py-1 pb-3 text-[10px] text-gray-500 flex items-center justify-between">
              <span>Build</span>
              <span className="font-mono text-gray-400" data-testid="m-build-date">{BUILD_DATE}</span>
            </div>
          </div>
        </div>
      )}

      {/* Offline Status */}
      <OfflineStatusBar />

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0" data-testid="mobile-content">
        <Outlet />
      </div>

      {/* Quick Appointment FAB - hidden on AdminDashboard */}
      {!location.pathname.includes("dashboard-admin") && !location.pathname.includes("projekte") && <MobileQuickAppointmentFAB />}

      {/* ── Bottom Navigation ── */}
      <nav className="flex-shrink-0 safe-area-bottom tga-mobile-chrome" data-testid="mobile-bottom-nav"
        style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
      >
        <div className="flex items-center justify-around h-16">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.to);
            return (
              <button key={item.to}
                onClick={() => navigate(item.to, { replace: true })}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[56px] h-full transition-all ${isActive ? "text-blue-400 scale-105" : "text-gray-500"}`}
                data-testid={item.testId}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[9px] font-medium leading-tight">{t(item.labelKey, item.labelFallback)}</span>
              </button>
            );
          })}
          {hasMore && (
            <button onClick={() => setShowMore(v => !v)}
              className={`flex flex-col items-center justify-center gap-0.5 min-w-[56px] h-full transition-all ${showMore ? "text-blue-400" : "text-gray-500"}`}
              data-testid="m-nav-more"
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[9px] font-medium leading-tight">{t("common.more", "Mehr")}</span>
            </button>
          )}
        </div>
      </nav>
    </div>
    </QueryClientProvider>
  );
}
